import { useState } from 'react';
import { Play, RotateCw, Check, X, Clock, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import AdminTabs from '../../components/admin/AdminTabs';
import { useAdminRuns, useAdminRunDetail } from '../../hooks/useAdminRuns';
import type { RunDto } from '@shared/types';

export default function AdminRuns() {
  const { list, trigger } = useAdminRuns();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [triggerMessage, setTriggerMessage] = useState<{
    type: 'ok' | 'err';
    text: string;
    url?: string;
  } | null>(null);

  const onTrigger = async () => {
    setTriggerMessage(null);
    try {
      const res = await trigger.mutateAsync(undefined);
      setTriggerMessage({
        type: 'ok',
        text: res.data.message,
        url: res.data.githubActionsUrl,
      });
    } catch (err) {
      setTriggerMessage({
        type: 'err',
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <div>
      <h1 className="mb-2 font-heading text-2xl font-semibold text-dark">Administrace</h1>
      <AdminTabs />

      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {list.data
            ? `${list.data.length} běhů (posledních 50)`
            : 'Načítám…'}
        </div>
        <button
          onClick={onTrigger}
          disabled={trigger.isPending}
          className="btn-primary"
        >
          {trigger.isPending ? <RotateCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {trigger.isPending ? 'Spouštím…' : 'Spustit ručně'}
        </button>
      </div>

      {triggerMessage && (
        <div
          className={`mb-4 rounded-md border p-3 text-sm ${
            triggerMessage.type === 'ok'
              ? 'border-positive/40 bg-positive-bg text-positive'
              : 'border-danger/40 bg-danger/10 text-danger'
          }`}
        >
          {triggerMessage.text}
          {triggerMessage.url && (
            <a
              href={triggerMessage.url}
              target="_blank"
              rel="noreferrer"
              className="ml-2 inline-flex items-center gap-1 underline"
            >
              GitHub Actions <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}

      <div className="space-y-2">
        {list.data?.map((r) => (
          <RunRow
            key={r.id}
            run={r}
            expanded={expandedId === r.id}
            onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
          />
        ))}
        {list.data?.length === 0 && (
          <div className="card p-6 text-center text-sm text-gray-500">
            Zatím žádný běh. Spusť první ingest tlačítkem nahoře nebo počkej na cron 06:00 UAE.
          </div>
        )}
      </div>
    </div>
  );
}

function statusIcon(status: string) {
  if (status === 'success') return <Check className="h-4 w-4 text-positive" />;
  if (status === 'failed') return <X className="h-4 w-4 text-danger" />;
  if (status === 'running') return <Clock className="h-4 w-4 text-warning" />;
  return <Clock className="h-4 w-4 text-gray-400" />;
}

function RunRow({
  run,
  expanded,
  onToggle,
}: {
  run: RunDto;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          {statusIcon(run.status)}
          <div>
            <div className="text-sm font-semibold text-dark">{formatDate(run.startedAt)}</div>
            <div className="text-xs text-gray-500">
              triggered: {run.triggeredBy} · status: {run.status}
              {run.finishedAt && run.startedAt && (
                <> · trvání: {((run.finishedAt - run.startedAt) | 0)}s</>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {run.stats && <StatsSummary stats={run.stats} />}
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>
      {expanded && <RunDetail id={run.id} />}
    </div>
  );
}

function StatsSummary({ stats }: { stats: Record<string, number | string> }) {
  const fetched = Number(stats.fetched ?? 0);
  const fresh = Number(stats.fresh ?? 0);
  const selected = Number(stats.selected ?? 0);
  return (
    <div className="hidden text-xs text-gray-500 sm:block">
      {fetched > 0 && <>fetched <strong className="text-dark">{fetched}</strong> · </>}
      {fresh > 0 && <>fresh <strong className="text-dark">{fresh}</strong> · </>}
      <>selected <strong className="text-dark">{selected}</strong></>
    </div>
  );
}

function RunDetail({ id }: { id: string }) {
  const { data, isLoading } = useAdminRunDetail(id);

  if (isLoading || !data) {
    return (
      <div className="border-t border-gray-200 p-4 text-sm text-gray-500">Načítám…</div>
    );
  }

  return (
    <div className="space-y-4 border-t border-gray-200 bg-gray-50 p-4">
      {data.run.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-xs text-danger">
          {data.run.error}
        </div>
      )}

      {data.run.stats && (
        <div className="card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Stats</h3>
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            {Object.entries(data.run.stats).map(([k, v]) => (
              <div key={k}>
                <div className="text-gray-500">{k}</div>
                <div className="font-mono font-semibold text-dark">{String(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.items.length > 0 && (
        <div className="card p-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
            Vybrané články ({data.items.length})
          </h3>
          <ol className="space-y-1 text-sm">
            {data.items.map((it) => (
              <li key={it.rank} className="flex gap-3">
                <span className="w-6 text-right text-xs text-gray-400">#{it.rank}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wide">{it.category}</span>
                <span className="flex-1 truncate text-dark">{it.titleCz}</span>
                <span className="text-xs text-gray-500">{it.sourceName}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.logs.length > 0 && (
        <div className="card overflow-hidden">
          <h3 className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
            Job logs ({data.logs.length})
          </h3>
          <div className="max-h-[300px] overflow-y-auto p-3 font-mono text-xs">
            {data.logs.map((l, i) => (
              <div key={i} className={l.level === 'error' ? 'text-danger' : l.level === 'warn' ? 'text-warning' : 'text-gray-700'}>
                [{l.step}] {l.message}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  return d.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
